import { usePermissao } from "@/contexts/autorizacao/permissao";
import { useFormularioRespostaControle } from "@/contexts/fiscalizacao/formulario";
import { FiltroRelatoriosAM } from "@/contexts/fiscalizacao/relatorioAM/components/FiltroRelatoriosAM/FiltroRelatoriosAM";
import { ItemListFiltroRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/ItemListFiltroRelatorioAM/ItemListFiltroRelatorioAM";
import { ItemListRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/ItemListRelatorioAM/ItemListRelatorioAM";
import { SubmitBackupRAM } from "@/contexts/fiscalizacao/relatorioAM/components/SubmitBackupRAM";
import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { useMenu } from "@/shared/menu/useMenu";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { FilterOutlined, HomeOutlined, PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Col,
  Divider,
  Grid,
  List,
  Row,
  Tooltip,
  Typography,
} from "antd";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

const PAGE_SIZE = 20;

export function ListaRelatoriosAM() {
  const screens = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { isOnline } = useNetworkStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const { listaRelatorioAM, getRelatoriosAMApi, syncRelatoriosFromApi } =
    useRelatorioAM();
  const { hasEnabledPermissao } = usePermissao();
  const { message, notification } = App.useApp();
  const page = searchParams.get("page") || "1";
  const { countMunicipiosSynced } = useFormularioRespostaControle();
  const { atualizarMenuSelected } = useMenu();

  const currentPage = Number(page);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedTermos = listaRelatorioAM.slice(startIndex, endIndex);

  const {
    data: listaTermosFiltro,
    isLoading: isLoadingFiltro,
    refetch: refetchTermosFiltro,
    error: errorTermosFiltro,
  } = useQuery({
    queryKey: ["RAM_FILTRO", searchParams.toString()],
    enabled:
      isOnline &&
      searchParams.get("filtro") === "true" &&
      !!searchParams.get("estabelecimentoId"),

    queryFn: async () => {
      const response = await getRelatoriosAMApi({
        estabelecimentoId: Number(searchParams.get("estabelecimentoId")),
        page: currentPage,
        situacao: searchParams.get("situacao") || undefined,
      });
      return response;
    },
  });

  const { data: _ } = useQuery({
    queryKey: ["SYNC_RELATORIOS_AM", listaRelatorioAM],
    enabled: isOnline && listaRelatorioAM.length > 0,
    queryFn: async () => {
      await syncRelatoriosFromApi();
      message.success({
        content: "Relatórios sincronizados com sucesso",
        key: "sync-relatorios",
        duration: 3,
      });
      return true;
    },
  });

  const redirectToNovoRelatorio = () => {
    navigate("/RAM/novo");
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Lista de Relatórios de Auditoria e Monitoria";

    if (!isOnline && searchParams.get("filtro") === "true") {
      searchParams.delete("filtro");
      setSearchParams(searchParams);
    }

    if (errorTermosFiltro) {
      const messageError = formatMessageError(errorTermosFiltro);
      notification.error({
        message:
          "Não foi possivel carregar a lista de relatórios de auditoria e monitoria.",
        description: messageError,
        key: "error-filtro",
      });
    }
  }, [errorTermosFiltro]);

  return (
    <>
      <Typography.Title
        level={screens.xs ? 4 : 3}
        style={{ textAlign: "center" }}
      >
        Lista de Relatórios de Auditoria e Monitoria
      </Typography.Title>

      {countMunicipiosSynced === 0 && (
        <Alert
          message="Parece que você ainda não sincronizou nenhum município para uso offline. Recomendamos que você sincronize pelo menos um município para garantir o acesso aos formulários de controle de resposta."
          type="warning"
          showIcon
          action={
            <Button
              type="primary"
              onClick={() => {
                navigate("/");
                atualizarMenuSelected(["inicio"]);
              }}
              icon={<HomeOutlined />}
            >
              Ir para página inicial
            </Button>
          }
        />
      )}

      <Divider />

      <Row gutter={16} justify="space-between" align="middle">
        <Col>
          <Row gutter={8}>
            <Col>
              <Tooltip title="Filtro" destroyOnHidden>
                <Button
                  onClick={() => {
                    if (searchParams.get("filtro") === "true") {
                      searchParams.delete("filtro");
                      searchParams.delete("estabelecimentoId");
                      searchParams.delete("situacao");
                      searchParams.delete("page");
                      setSearchParams(searchParams);
                      message.info({
                        content:
                          "Filtro limpo, exibindo lista de relatórios sincronizados.",
                        key: "filtro",
                      });
                    } else {
                      searchParams.set("filtro", "true");
                    }
                    setSearchParams(searchParams);
                  }}
                  danger={searchParams.get("filtro") === "true"}
                >
                  <FilterOutlined />
                  {searchParams.get("filtro") === "true" && " Fechar"}
                </Button>
              </Tooltip>
            </Col>

            <Col>
              <Tooltip title="Novo Relatório">
                <Button
                  type="primary"
                  onClick={redirectToNovoRelatorio}
                  disabled={!hasEnabledPermissao("RAM_CRIAR")}
                >
                  <PlusOutlined />
                </Button>
              </Tooltip>
            </Col>
          </Row>
        </Col>

        <Col>
          <SubmitBackupRAM />
        </Col>
      </Row>

      {searchParams.get("filtro") === "true" && (
        <Row>
          <Col span={24} style={{ marginTop: 16 }}>
            <FiltroRelatoriosAM />
          </Col>
        </Row>
      )}

      {searchParams.get("filtro") === "true" && (
        <>
          <Divider>
            <Typography.Text strong>Resultados da busca</Typography.Text>
          </Divider>
          <List
            size="small"
            bordered
            dataSource={listaTermosFiltro?.content || []}
            renderItem={(item, index) => (
              <ItemListFiltroRelatorioAM
                key={index}
                item={item}
                reloadItems={() => refetchTermosFiltro()}
              />
            )}
            loading={isLoadingFiltro}
            pagination={{
              pageSize: PAGE_SIZE,
              position: "bottom",
              defaultCurrent: 1,
              current: Number(page),
              total: listaTermosFiltro?.totalElements || 0,
              showSizeChanger: false,
              onChange: (page) => {
                searchParams.set("page", String(page));
                setSearchParams(searchParams);
              },
              align: "end",
              size: "small",
            }}
          />
        </>
      )}

      {!searchParams.get("filtro") && (
        <>
          <Divider>
            <Typography.Text strong>Relatórios sincronizados</Typography.Text>
          </Divider>

          <List
            size="small"
            bordered
            dataSource={paginatedTermos}
            renderItem={(item, index) => (
              <ItemListRelatorioAM key={index} item={item} />
            )}
            pagination={{
              pageSize: PAGE_SIZE,
              position: "bottom",
              defaultCurrent: 1,
              current: Number(page),
              total: listaRelatorioAM.length,
              onChange: (page) => setSearchParams({ page: String(page) }),
              showSizeChanger: false,
              align: "end",
              size: "small",
            }}
          />
        </>
      )}
    </>
  );
}
